import {
  Box,
  Radio,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Snapshot, FileSnapshot } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useRef } from 'react';

import { Loader } from '@/components/layout/loading';
import { hasSecurityIssue } from '@/components/security/securityUtils';
import { SkullIcon } from '@/components/security/skullIcon';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { SnapshotSelectionEmptyState } from '@/components/snapshots/snapshotSelection/snapshotSelectionEmptyState';
import { BorderlessTableCell } from '@/components/table/components';
import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { DATE_FORMAT, dayjs, TIME_FORMAT } from '@/utils/dayjs';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

interface SnapshotsTableProps {
  selectedMonth: Date;
  inventorySnapshots: Snapshot[];
  onRowSelectionChange: (snapshot: FileSnapshot | undefined) => void;
  vaultId: string;
  resourceId: string;
  filePath: string;
  selectedSnapshot: FileSnapshot | undefined;
}

const ThinTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    width: `100px`,
    paddingRight: '0',
  },
}));

export function SnapshotsList(props: SnapshotsTableProps) {
  const {
    onRowSelectionChange,
    inventorySnapshots,
    selectedMonth,
    vaultId,
    resourceId,
    filePath,
    selectedSnapshot,
  } = props;
  const dal = useDAL();
  const { vaults } = useBackupVaults();
  const vaultBgColor = vaults.find((v) => v.id === vaultId)?.backgroundColor;
  const ref = useRef<HTMLTableElement>(null);

  const end = dayjs.utc(selectedMonth).isSame(dayjs.utc(), 'month')
    ? new Date()
    : dayjs.utc(new Date(selectedMonth)).endOf('month').toDate();
  const start = dayjs.utc(new Date(selectedMonth)).startOf('month').toDate();
  const { data: fileSnapshotsBody, isLoading: isLoadingFileSnapshots } =
    dal.explorer.useListPathSnapshots(resourceId, filePath, start, end, 0, 100);

  const fileSnapshots: FileSnapshot[] = useMemo(
    () =>
      (fileSnapshotsBody?.data || [])
        .filter((fileSnap) =>
          inventorySnapshots.find(
            (inventorySnap) => inventorySnap.id === fileSnap.snapshotId
          )
        )
        .sort((a, b) => b.generatedOn.getTime() - a.generatedOn.getTime()),
    [fileSnapshotsBody, inventorySnapshots]
  );

  useEffect(() => {
    onRowSelectionChange(fileSnapshots[0]);
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileSnapshots]);

  if (isLoadingFileSnapshots) {
    return (
      <Box marginTop='100px'>
        <Loader />
      </Box>
    );
  }

  if (fileSnapshots.length === 0) {
    return (
      <Box marginTop='100px'>
        <SnapshotSelectionEmptyState description='No Eon snapshots available' />
      </Box>
    );
  }

  return (
    <TableContainer ref={ref}>
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                textTransform: 'uppercase',
                padding: 0,
                backgroundColor: 'var(--mui-palette-background-paper)',

                '& div': {
                  fontWeight: 'normal',
                  marginY: '8px',
                  padding: '12px',
                  backgroundColor: 'var(--mui-palette-background-section)',
                },

                '&:last-of-type': {
                  '& div': {
                    marginRight: 0,
                    paddingRight: '0',
                  },
                },
              },
            }}
          >
            <ThinTableCell>
              <Box>
                <Typography variant='subtitle1' height='20px'>
                  {/* for having proper background color */}{' '}
                </Typography>
              </Box>
            </ThinTableCell>
            <TableCell>
              <Box>
                <Typography variant='subtitle1'>Snapshot time (UTC)</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box>
                <Typography variant='subtitle1'>Size</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box>
                <Typography variant='subtitle1'>Last modified (UTC)</Typography>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fileSnapshots.map((snapshot) => (
            <TableRow
              onClick={() => {
                onRowSelectionChange(snapshot);
              }}
              key={snapshot.snapshotId}
              className='cursor-pointer'
              sx={{
                '& td': {
                  padding: '12px',
                  borderBottom: '1px solid var(--mui-palette-divider)',

                  '&:first-of-type': {
                    paddingLeft: '36px',
                    paddingRight: '0',
                  },

                  '&:last-of-type': {
                    paddingRight: '0',
                  },
                },
              }}
            >
              <BorderlessTableCell>
                <Stack
                  gap='8px'
                  padding='0'
                  direction='row'
                  alignItems='center'
                >
                  <Radio
                    size='small'
                    checked={
                      selectedSnapshot?.snapshotId === snapshot.snapshotId
                    }
                  />
                  {vaultBgColor && (
                    <SnapshotIndicator
                      sx={{ margin: '6px' }}
                      backgroundColor={vaultBgColor}
                    />
                  )}
                </Stack>
              </BorderlessTableCell>
              <BorderlessTableCell>
                <Stack
                  gap='8px'
                  padding='0'
                  direction='row'
                  alignItems='center'
                >
                  <Typography width='100px' variant='body1'>
                    {dayjs.utc(snapshot.generatedOn).format(DATE_FORMAT)}
                  </Typography>
                  <Typography width='70px' variant='body1'>
                    {dayjs.utc(snapshot.generatedOn).format(TIME_FORMAT)}
                  </Typography>
                  {hasSecurityIssue(snapshot) && (
                    <Tooltip title='This snapshot is infected with ransomware.'>
                      <Stack>
                        <SkullIcon />
                      </Stack>
                    </Tooltip>
                  )}
                </Stack>
              </BorderlessTableCell>
              <BorderlessTableCell>
                <Typography variant='body1'>
                  {fileSizeFormatter(snapshot.size)}
                </Typography>
              </BorderlessTableCell>
              <BorderlessTableCell>
                <Stack
                  gap='8px'
                  padding='0'
                  direction='row'
                  alignItems='center'
                >
                  <Typography width='100px' variant='body1'>
                    {dayjs.utc(snapshot.modifiedOn).format(DATE_FORMAT)}
                  </Typography>
                  <Typography width='70px' variant='body1'>
                    {dayjs.utc(snapshot.modifiedOn).format(TIME_FORMAT)}
                  </Typography>
                </Stack>
              </BorderlessTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
